/**
 * A composition's art-slot offers, its ONE media-promise assembly, and the
 * deterministic default art direction for its slots.
 */
import type { GameDesign } from '../design/types.js';
import {
  COMPOSITION_ART_SLOT_MAX_LEN,
  COMPOSITION_ART_STYLE_MAX_LEN,
  COMPOSITION_MEDIA_KIND,
  compositionMediaExecutionV1,
  validateCompositionArtDirectionV1,
  type CompositionArtDirectionV1,
  type CompositionMediaV1,
  type CompositionGameSpecV1,
} from './gameSpec.js';
import { COMPOSITION_BACKGROUND_SLOT, compositionArtSlotsV1, compositionMediaSlotRolesV1, compositionMediaSlotsV1 } from './compositionPresentation.js';

/** One generated-art slot a composition offers. */
export interface CompositionMediaSlotOfferV1 {
  slot: string;
  role: string;
}

/** The art-directed slots a spec offers (each kind's sprite plus the
 * background), each with its role line. Strips, the cover and the soundtrack
 * are generated from these and need no separate review line. */
export function compositionMediaSlotOffersV1(spec: CompositionGameSpecV1): CompositionMediaSlotOfferV1[] {
  const roles = compositionMediaSlotRolesV1(spec);
  return compositionArtSlotsV1(spec).map(slot => ({ slot, role: roles[slot]! }));
}

/** The ONE assembly of a media promise: the spec's derived slots (the
 * soundtrack only when the platform music switch admits it), the reviewed art
 * direction, and — for a released runtime — the derived paid execution
 * allowance. Local (unreleased) authorities stay zero-provider. */
export function compositionMediaV1(
  spec: CompositionGameSpecV1,
  artDirection: unknown,
  options: { released: boolean; music: boolean },
): CompositionMediaV1 {
  return {
    kind: COMPOSITION_MEDIA_KIND,
    slots: compositionMediaSlotsV1(spec, { music: options.music }),
    artDirection: validateCompositionArtDirectionV1(artDirection, compositionArtSlotsV1(spec)),
    ...(options.released ? { execution: compositionMediaExecutionV1(spec, { music: options.music }) } : {}),
  };
}

/** Normalize free text into one bounded art-direction line: collapse
 * whitespace, drop markup/control characters, trim, and cap the length. */
export function compositionArtTextV1(text: string, max: number): string {
  const line = text.replace(/[\u0000-\u001f\u007f<>{}`]+/g, ' ').replace(/\s+/g, ' ').trim();
  return line.length <= max ? line : line.slice(0, max).trimEnd();
}

/** Deterministic art direction for a composition from the creator's own
 * idea: its style/theme and scenery carry over; each slot keeps its role,
 * themed by the game's title. The compose plan may supply a richer per-slot
 * mapping (`composedArtDirectionV1`). */
export function deriveCompositionArtDirectionV1(input: {
  title: string;
  slots: readonly CompositionMediaSlotOfferV1[];
  design?: GameDesign | null;
}): CompositionArtDirectionV1 {
  const title = compositionArtTextV1(input.title, 80) || 'this game';
  const design = input.design ?? null;
  const authored = [design?.artDirection?.style, design?.artDirection?.mood, design?.theme]
    .map(value => compositionArtTextV1(value ?? '', COMPOSITION_ART_STYLE_MAX_LEN)).filter(Boolean);
  const style = compositionArtTextV1(authored.length ? authored.join('; ')
    : `Bright, readable 2D game art for "${title}"`, COMPOSITION_ART_STYLE_MAX_LEN);
  const scenery = compositionArtTextV1(design?.scenery?.backgrounds?.[0]?.prompt ?? '', COMPOSITION_ART_SLOT_MAX_LEN);
  const slots = Object.fromEntries(input.slots.map(({ slot, role }) => [slot,
    slot === COMPOSITION_BACKGROUND_SLOT && scenery
      ? scenery
      : compositionArtTextV1(`${role}, in the world of "${title}"`, COMPOSITION_ART_SLOT_MAX_LEN)]));
  return validateCompositionArtDirectionV1({ style, slots }, input.slots.map(offer => offer.slot));
}
